import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "../../../../utils/Date";
import { statusValueFormatter } from "../../../../utils/Formatter/Global";
import { dateFilterParams } from "../../../../utils/Formatter/AgGridFilter";

import CustomizedStatus from "../../../Custom/CustomizedStatus";
import CustomizedAvatar from "../../../Custom/CustomizedAvatar";

import { usePurchaseAgGrid } from "../../../../hooks/Purchase/use-purchase-aggrid";

export const usePurchaseRequestColumnDefs = () => {
  const { t } = useTranslation();
  const [columnDef, setColumnDef] = useState<ColDef[]>();

  const { purchaseUserFilterParams, branchFilterParams } =
    usePurchaseAgGrid("request");

  useEffect(() => {
    setColumnDef([
      {
        field: "unique_id",
        headerName: t("sales.unique_id"),
        filter: "agTextColumnFilter",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "issue_date",
        headerName: t("date.issue_date"),
        sort: "desc",
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "expected_date",
        headerName: t("date.within_date"),
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "status",
        headerName: t("status.index"),
        filter: "agSetColumnFilter",
        cellRenderer: (params: ICellRendererParams<any, string>) => (
          <CustomizedStatus status={params.value} />
        ),
        cellStyle: {
          display: "flex",
          justifycontent: "center",
          alignItems: "center",
        },
        minWidth: 200,
        flex: 1,
        filterParams: {
          values: [
            "draft",
            "wait_approve",
            "not_approved",
            "approved",
            "partially_ordered",
            "fully_ordered",
            "cancelled",
          ],
          valueFormatter: (params: ValueFormatterParams) =>
            statusValueFormatter(params.value),
        },
      },
      {
        field: "branch_id",
        hide: true,
        suppressColumnsToolPanel: true,
        filter: "agNumberColumnFilter",
      },
      {
        field: "branch_unique_id_name",
        headerName: t("branch.index"),
        minWidth: 200,
        flex: 1,
        filter: "agSetColumnFilter",
        filterParams: branchFilterParams,
      },
      {
        field: "requester",
        headerName: t("purchase.request.requester"),
        filter: "agTextColumnFilter",
        cellRenderer: (params: ICellRendererParams<any, any>) => {
          if (params.data && params.data.requester_object) {
            return (
              <CustomizedAvatar avatars={[params.data.requester_object]} />
            );
          }
        },
        minWidth: 200,
        flex: 1,
      },
      {
        field: "related_user_object_list",
        headerName: t("sales.employee_list"),
        filter: "agSetColumnFilter",
        filterParams: purchaseUserFilterParams,
        cellRenderer: (params: ICellRendererParams<any, any>) => {
          if (params.value && params.value.length > 0) {
            return <CustomizedAvatar avatars={params.value} />;
          }
        },
        minWidth: 200,
        flex: 1,
      },
      {
        field: "remark",
        headerName: t("sentence.remark"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "tag_list",
        headerName: t("sales.tag_list"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "department",
        headerName: t("user.account.department"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "position",
        headerName: t("user.account.position"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "reference_no",
        headerName: t("purchase.reference_no"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "created_date",
        headerName: t("date.created_date"),
        hide: true,
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "last_updated_date",
        headerName: t("date.last_updated_date"),
        hide: true,
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "reference_document_list",
        headerName: t("purchase.reference_unique_id"),
        hide: true,
        suppressColumnsToolPanel: true,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return columnDef;
};
